export type OvertimeReviewStatus = 'pending' | 'reviewed' | 'needs_review' | 'cancelled';

export interface OvertimeReport {
  id: string;
  attendanceId: string;
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  workDate: string;
  startAt: string;
  endAt: string;
  durationMinutes: number;
  reason: string;
  status: OvertimeReviewStatus;
  adminNote: string | null;
  submittedAt: string;
  updatedAt: string;
  reviewedAt: string | null;
  version: number;
}

export interface OvertimeContext {
  eligible: boolean;
  reason: string | null;
  scheduledStartAt: string | null;
  scheduledEndAt: string | null;
  clockInAt: string | null;
  clockOutAt: string | null;
  workDate: string;
  report: OvertimeReport | null;
}

export type OvertimeClient = {
  rpc: (name: string, parameters?: Record<string, unknown>) => PromiseLike<{ data: any; error: any }>;
  from: (table: string) => any;
};

const first = (value: any): any => Array.isArray(value) ? value[0] : value;
const text = (row: any, camel: string, snake: string): string => String(row[camel] ?? row[snake] ?? '');

export const normalizeOvertimeReport = (value: unknown): OvertimeReport | null => {
  const row = first(value);
  if (!row || typeof row !== 'object') return null;
  const id = text(row, 'id', 'id');
  const attendanceId = text(row, 'attendanceId', 'attendance_id');
  const startAt = text(row, 'startAt', 'start_at');
  const endAt = text(row, 'endAt', 'end_at');
  const version = Number(row.version);
  const status = row.status as OvertimeReviewStatus;
  const durationMinutes = Number(row.durationMinutes ?? row.duration_minutes);
  if (!id || !attendanceId || !Number.isFinite(Date.parse(startAt)) || !Number.isFinite(Date.parse(endAt)) ||
      !Number.isInteger(version) || version < 1 || !Number.isFinite(durationMinutes) ||
      !['pending', 'reviewed', 'needs_review', 'cancelled'].includes(status)) return null;
  return {
    id, attendanceId, startAt, endAt, version, status, durationMinutes,
    employeeId: text(row, 'employeeId', 'employee_id'),
    employeeCode: text(row, 'employeeCode', 'employee_code'),
    employeeName: text(row, 'employeeName', 'employee_name'),
    workDate: text(row, 'workDate', 'work_date'),
    reason: String(row.reason ?? ''),
    adminNote: row.adminNote ?? row.admin_note ?? null,
    submittedAt: text(row, 'submittedAt', 'submitted_at'),
    updatedAt: text(row, 'updatedAt', 'updated_at'),
    reviewedAt: row.reviewedAt ?? row.reviewed_at ?? null
  };
};

export const loadOvertimeContext = async (
  client: OvertimeClient, employeeCode: string, attendanceId: string
): Promise<OvertimeContext> => {
  const { data, error } = await client.rpc('attendance_overtime_context', {
    p_employee_code: employeeCode, p_attendance_id: attendanceId
  });
  if (error) throw new Error(error.message ?? '勤務予定を確認できませんでした。');
  const row = first(data);
  if (!row || typeof row.eligible !== 'boolean' || !row.workDate) {
    throw new Error('時間外勤務の判定結果を確認できませんでした。');
  }
  const report = row.report == null ? null : normalizeOvertimeReport(row.report);
  if (row.report && !report) throw new Error('保存された時間外報告を確認できませんでした。');
  return {
    eligible: row.eligible, reason: row.reason ?? null,
    scheduledStartAt: row.scheduledStartAt ?? null,
    scheduledEndAt: row.scheduledEndAt ?? null,
    clockInAt: row.clockInAt ?? null, clockOutAt: row.clockOutAt ?? null,
    workDate: row.workDate, report
  };
};

export interface SubmitOvertimeInput {
  employeeCode: string;
  attendanceId: string;
  startAt: string;
  endAt: string;
  reason: string;
  expectedVersion: number | null;
}

const matchesInput = (report: OvertimeReport | null, input: SubmitOvertimeInput): boolean =>
  Boolean(report && report.attendanceId === input.attendanceId && report.employeeCode === input.employeeCode &&
    Date.parse(report.startAt) === Date.parse(input.startAt) && Date.parse(report.endAt) === Date.parse(input.endAt) &&
    report.reason === input.reason.trim() && report.version > (input.expectedVersion ?? 0));

// A lost response must not make an already stored report look unsaved or cause a second write.
export const submitOvertimeReport = async (
  client: OvertimeClient, input: SubmitOvertimeInput
): Promise<OvertimeReport> => {
  let writeError: unknown = null;
  try {
    const { data, error } = await client.rpc('submit_attendance_overtime', {
      p_employee_code: input.employeeCode, p_attendance_id: input.attendanceId,
      p_start_at: input.startAt, p_end_at: input.endAt, p_reason: input.reason.trim(),
      p_expected_version: input.expectedVersion
    });
    if (error) throw error;
    const report = normalizeOvertimeReport(data);
    if (matchesInput(report, input)) return report!;
    writeError = new Error('保存された報告と入力内容が一致しませんでした。');
  } catch (error) {
    writeError = error;
  }
  try {
    const context = await loadOvertimeContext(client, input.employeeCode, input.attendanceId);
    if (matchesInput(context.report, input)) return context.report!;
    if (context.report && context.report.version !== input.expectedVersion) {
      throw new Error('報告が別の画面で更新されています。入力内容を控え、最新の報告を読み直してください。');
    }
  } catch (readError) {
    if (readError instanceof Error && readError.message.includes('別の画面')) throw readError;
  }
  const detail = writeError && typeof writeError === 'object' && 'message' in writeError
    ? String((writeError as { message: unknown }).message) : '';
  throw new Error(`報告の保存を確認できませんでした。退勤打刻は保存済みです。${detail ? ` ${detail}` : ''}`);
};

// Attendance is operated in Japan; explicit JST keeps date boundaries stable on any device.
export const toJstDateTimeInput = (value: string | null): string => {
  if (!value || !Number.isFinite(Date.parse(value))) return '';
  return new Date(Date.parse(value) + 9 * 60 * 60 * 1000).toISOString().slice(0, 16);
};

export const fromJstDateTimeInput = (value: string): string | null => {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const date = new Date(`${value}:00+09:00`);
  if (!Number.isFinite(date.getTime())) return null;
  const iso = date.toISOString();
  return toJstDateTimeInput(iso) === value ? iso : null;
};

export const validateOvertimePeriod = (
  context: OvertimeContext, start: string, end: string, reason: string
): { startAt: string; endAt: string; durationMinutes: number } | string => {
  const startAt = fromJstDateTimeInput(start);
  const endAt = fromJstDateTimeInput(end);
  if (!context.eligible || !context.clockOutAt || !context.clockInAt || !context.scheduledEndAt) {
    return '時間外勤務の対象となる退勤記録を確認できません。';
  }
  if (!startAt || !endAt) return '時間外勤務の開始・終了日時を入力してください。';
  const startMs = Date.parse(startAt);
  const endMs = Date.parse(endAt);
  if (endMs <= startMs) return '終了日時は開始日時より後にしてください。日をまたぐ場合は終了日も変更してください。';
  if (endMs - startMs > 24 * 60 * 60 * 1000) return '1件の時間外勤務は24時間以内で入力してください。';
  const minuteBoundary = (value: string) => Math.floor(Date.parse(value) / 60000) * 60000;
  if (startMs < Math.max(minuteBoundary(context.clockInAt), minuteBoundary(context.scheduledEndAt))) {
    return '開始日時は出勤時刻・予定終了時刻以降にしてください。';
  }
  if (endMs > minuteBoundary(context.clockOutAt)) return '終了日時は保存された退勤時刻までにしてください。';
  if (!reason.trim()) return '時間外勤務をした理由を入力してください。';
  if (reason.trim().length > 2000) return '理由は2,000文字以内で入力してください。';
  return { startAt, endAt, durationMinutes: Math.floor((endMs - startMs) / 60000) };
};

export const formatOvertimeDateTime = (value: string | null): string => {
  const formatted = toJstDateTimeInput(value);
  return formatted ? formatted.replace('T', ' ') : '—';
};

export const formatOvertimeMinutes = (value: number): string => `${Math.floor(value / 60)}時間${value % 60}分`;

export const OVERTIME_STATUS_LABELS: Record<OvertimeReviewStatus, string> = {
  pending: '未確認', reviewed: '確認済み', needs_review: '要確認', cancelled: '取消済み'
};
